// 米問屋（里の見出しから開く1画面 #/komedoiya）。課金「米問屋の通い帳」で、兵糧を掃討チケットに替える（core の exchangeKomedoiya）。
// 手形が無ければ交換の比率とショップへの導線だけ。上に看板娘のこまち（たぬき娘）と一言、下に交換と兵糧の商品（蔵出し。今日の分が無ければ売り切れ。月パスは種類が違うので出さない）
import { hasKomedoiya, META_CONFIG, PRODUCT_BY_ID, PRODUCTS } from '@hyakki/core';
import { sfx } from '../audio';
import { api } from '../api';
import { go } from '../router';
import { act, state } from '../state';
import { esc, fmt, resIcon } from './dom';
import { daypart, KOMACHI, pick } from './shoplines';
import { mountShopScene } from './shopscene';
import { icon } from './icons';
import { openModal, toast } from './overlay';
import { errorText } from './status';

const { hyoro: H, tickets: T } = META_CONFIG.komedoiya;
const CAP = META_CONFIG.sweep.ticketCap;
const KURA = META_CONFIG.kuradashi.hyoro;

/** 今の兵糧（本体＋予備。蔵出しで買った分も替えられる） */
const hyoroNow = () => (state.stamina ? state.stamina.value + state.stamina.reserve : (state.resources.hyoro ?? 0));
/** 今の兵糧と所持の上限から、何回替えられるか */
function maxTimes() {
  return Math.max(0, Math.min(Math.floor(hyoroNow() / H), Math.floor((CAP - (state.resources.soutou ?? 0)) / T)));
}

/** 今の状態のこまちの一言（通い帳なし・チケットが持ちきれない・兵糧不足・交換できる） */
function stateLine(): string {
  const have = state.resources.soutou ?? 0;
  if (!state.profile || !hasKomedoiya(state.profile)) return pick(KOMACHI.locked);
  if (have + T > CAP) return pick(KOMACHI.full);
  if (maxTimes() < 1) return pick(KOMACHI.short, { n: fmt(H - hyoroNow()) });
  return pick(KOMACHI.ready);
}

/** 兵糧の商品の行（万屋と同じ形）。押すとショップのその商品へ。soldOut なら押せない「売り切れ」 */
function buyRow(id: string, art: string, sub: string, soldOut = false) {
  const p = PRODUCT_BY_ID[id];
  return p
    ? `<li class="yz-good">${art}<span class="yz-gn"><b>${esc(p.name.replace(/（.*）$/, ''))}</b><small>${sub}</small></span>${
        soldOut ? `<button class="btn primary yz-buy" disabled>売り切れ</button>` : `<button class="btn gold yz-buy" data-buy="${id}">¥${fmt(p.yen)}</button>`
      }</li>`
    : '';
}
/** 蔵出し：ショップで売っていれば出す（今日の分が無ければ売り切れ）。売っていない・ゲストは null */
type Kura = 'open' | 'sold' | null;

/** 所持・下の枠（交換の行＋兵糧の商品）。どちらも万屋と同じ行の形 */
function view(n: number, kura: Kura): { wallet: string; panel: string } {
  const hy = hyoroNow();
  const have = state.resources.soutou ?? 0;
  const owned = !!state.profile && hasKomedoiya(state.profile);
  const max = maxTimes();
  const wallet = `<span>${resIcon('hyoro')}<b>${fmt(hy)}</b></span><span>${resIcon('soutou')}<b>${fmt(have)}</b><small>/${fmt(CAP)}</small></span>`;
  const k = clampN(n);
  // 1行目：掃討チケット ＋◯枚（兵糧 −◯）と交換。2行目：− スライダー ＋ 最大（替えられるときだけ）。通い帳が無ければ通い帳へ
  const btn = !owned
    ? `<button class="btn gold yz-buy" data-go>${icon('lock')}通い帳</button>`
    : `<button class="btn primary yz-buy" data-do ${max < 1 ? 'disabled' : ''}>交換</button>`;
  const bar =
    owned && max >= 1
      ? `<div class="kd-bar">
      <button class="btn kd-n" data-d="-1" aria-label="10枚減らす">${icon('minus')}</button>
      <input type="range" class="kd-range" min="1" max="${max}" value="${k}" aria-label="交換する枚数">
      <button class="btn kd-n" data-d="1" aria-label="10枚増やす">${icon('plus')}</button>
      <button class="btn kd-n kd-max" data-max>最大</button>
    </div>`
      : '';
  const exRow = `<li class="yz-good kd-row">${resIcon('soutou').replace('class="ri ', 'class="ri yz-gi ')}
    <span class="yz-gn"><b>掃討チケット <em class="kd-so">+${fmt(T * k)}</em>枚</b><small class="kd-sum">${resIcon('hyoro')}兵糧 <span class="kd-hy">-${fmt(H * k)}</span></small></span>${btn}${bar}</li>`;
  const goods = kura ? `<h3 class="yz-h">兵糧</h3><ul class="yz-goods">${buyRow('kuradashi', `<i class="ri ri-hyoro yz-gi"></i>`, `兵糧+${KURA}`, kura === 'sold')}</ul>` : '';
  return { wallet, panel: `<h3 class="yz-h">交換</h3><ul class="yz-goods">${exRow}</ul>${goods}` };
}

const clampN = (n: number) => Math.max(1, Math.min(n, Math.max(1, maxTimes())));

/** 里の見出しの「米問屋」から */
export const openKomedoiya = () => go('komedoiya');

/** 米問屋の1画面（screens/shops.ts の mount） */
export function mountKomedoiya(el: HTMLElement) {
  let n = 1;
  let kura: Kura = null;
  // 押すたびに「今の状態の一言」と雑談を交互に
  let pokes = 0;
  const sc = mountShopScene(el, 'komedoiya', '米問屋', { src: '/art/shopgirls/komachi.webp', name: 'こまち' }, () => sc.say(pokes++ % 2 ? pick(KOMACHI.poke) : stateLine()));
  const body = sc.panel;
  body.classList.add('kd');
  // スライダーを動かしているあいだは作り直さず、数字とボタンだけ替える
  const setN = (v: number) => {
    n = clampN(v);
    const max = maxTimes();
    const q = <T extends Element>(s: string) => body.querySelector<T>(s);
    q('.kd-hy')!.textContent = `-${fmt(H * n)}`;
    q('.kd-so')!.textContent = `+${fmt(T * n)}`;
    const range = q<HTMLInputElement>('.kd-range');
    if (!range) return;
    range.value = String(n);
    range.style.setProperty('--kd-v', String(max > 1 ? (n - 1) / (max - 1) : 1));
    q<HTMLButtonElement>('[data-d="-1"]')!.disabled = n <= 1;
    q<HTMLButtonElement>('[data-d="1"]')!.disabled = n >= max;
    q<HTMLButtonElement>('[data-max]')!.disabled = n >= max;
  };
  const redraw = () => {
    const v = view(n, kura);
    sc.wallet(v.wallet);
    body.innerHTML = v.panel;
    setN(n);
  };
  redraw();
  sc.say(pick(KOMACHI.greet[daypart()]));
  // 蔵出しは今日まだ買えるか（天下の石高・自分の本日残り）をショップの一覧で確かめてから出す。今日の分が無ければ売り切れ
  void api
    .products()
    .then((r) => {
      const k = r.products.find((p) => p.id === 'kuradashi');
      kura = !r.enabled || state.me?.user.guest || !k ? null : !k.kura?.koku || k.remainingToday === 0 ? 'sold' : 'open';
      if (kura && body.isConnected) redraw();
    })
    .catch(() => {});
  body.addEventListener('input', (e) => {
    const r = (e.target as HTMLElement).closest<HTMLInputElement>('.kd-range');
    if (r) setN(Number(r.value));
  });
  body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-go]')) return go('shop', { from: 'komedoiya' });
    const buy = t.closest<HTMLElement>('[data-buy]')?.dataset.buy;
    if (buy) return go('shop', { cat: buy === 'kuradashi' ? 'item' : 'perm', focus: buy, from: 'komedoiya' });
    const d = t.closest<HTMLButtonElement>('[data-d]');
    if (d && !d.disabled) return setN(n + Number(d.dataset.d));
    if (t.closest('[data-max]')) return setN(maxTimes());
    const b = t.closest<HTMLButtonElement>('[data-do]');
    if (!b || b.disabled) return;
    b.disabled = true;
    try {
      const r = await act(api.exchangeKomedoiya(n));
      sfx('coin');
      toast(`掃討チケット +${fmt(r.tickets)}`, 'good');
      sc.say(pick(KOMACHI.done, { n: fmt(r.tickets) }));
      n = 1;
    } catch (err) {
      toast(errorText(err), 'error');
    }
    redraw();
  });
}

const OUT_HINT_KEY = 'hk.ticketsOutHint';
/** 掃討チケットを使い切ったとき、米問屋の通い帳を持っていない人に購入の案内（ショップの通い帳へ）。「次回以降表示しない」まで毎回 */
export function ticketsOutHint(): Promise<void> {
  if ((state.resources.soutou ?? 0) > 0 || !state.profile || state.me?.user.guest || hasKomedoiya(state.profile)) return Promise.resolve();
  try {
    if (localStorage.getItem(OUT_HINT_KEY)) return Promise.resolve();
  } catch {
    /* 端末に残せなくても出すだけ */
  }
  const prod = PRODUCTS.find((x) => x.id === 'komedoiya');
  if (!prod) return Promise.resolve();
  return new Promise((done) => {
    const m = openModal(
      `<div class="kd kd-hint">
        <p class="kd-lead">米問屋の通い帳を買うと、兵糧を掃討チケットに交換できます</p>
        <div class="kd-rate"><span>${resIcon('hyoro')}<b>${H}</b></span>${icon('right')}<span>${resIcon('soutou')}<b>${T}</b></span></div>
        <p class="kd-price">買い切り <b>¥${fmt(prod.yen)}</b></p>
        <label class="kd-hide"><input type="checkbox" data-hide>次回以降表示しない</label>
        <div class="btn-row"><button class="btn" data-close>閉じる</button><button class="btn primary" data-shop>${icon('shop')}通い帳を見る</button></div>
      </div>`,
      {
        title: '掃討チケットがなくなりました',
        className: 'sheet-wrap navy-sheet kd-wrap',
        onClose: () => {
          if (m.body.querySelector<HTMLInputElement>('[data-hide]')?.checked) {
            try {
              localStorage.setItem(OUT_HINT_KEY, '1');
            } catch {
              /* 残せなければ次も出る */
            }
          }
          done();
        },
      },
    );
    m.body.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('[data-shop]')) return;
      m.close();
      go('shop', { cat: 'perm', focus: 'komedoiya', from: 'komedoiya' });
    });
  });
}
